import { DynamicForm } from "@shared/components/DynamicForm";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { FormField, FormService } from "@shared/services/FormServices";
import { Info, Plus, Save } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import ApplicationProcessFeesList from "../list/list";
import { Button } from "@shared/ui/button";
import {
  useCreateFeeConfiguration,
  useDeleteFeeConfiguration,
  usegetCategoryConfigMap,
  useGetCategoryWiseFees,
} from "@features/cx/settings/hooks";
import { toast } from "@shared/ui/use-toast";

interface Fee {
  id: string;
  name: string;
  description: string;
  amount: number;
  consumerCategory: string;
}

const ApplicationProcessFeesCreate = () => {
  const currency = useCurrency();
  const remoteUtilityId = useRemoteUtilityId();
  const [fees, setFees] = useState<Fee[]>([]);
  const { data: categoryConfigMap } = usegetCategoryConfigMap();
  const [consumerCategory, setConsumerCategory] = useState<string>("");
  const { mutateAsync: createFeeConfiguration } = useCreateFeeConfiguration();
  const { mutateAsync: deleteFeeConfiguration } = useDeleteFeeConfiguration();
  const { data: categoryWiseFees } = useGetCategoryWiseFees(consumerCategory);
  const consumerCategories = useMemo(() => {
    if (!categoryConfigMap || !Array.isArray(categoryConfigMap)) {
      return [];
    }
    return categoryConfigMap.map((category) => {
      const feeCount = fees.filter(
        (fee) => fee.consumerCategory === category.code
      ).length;
      return {
        value: category.code,
        label: `${category.name} (${feeCount} ${
          feeCount === 1 || feeCount === 0
            ? "fee configured"
            : "fees configured"
        })`,
        name: category.name,
      };
    });
  }, [categoryConfigMap, fees]);

  const formFields: FormField[] = [
    {
      name: "consumerCategory",
      label: "Select consumer category",
      type: "select",
      options: consumerCategories,
      required: true,
      defaultValue: consumerCategory || consumerCategories[0]?.value,
      classes: {
        select: "mb-2",
        label: "font-semibold text-base",
      },
    },
    {
      name: "name",
      label: "Fee Name",
      type: "text",
      required: true,
      placeholder: "e.g. Application Fee",
      group: `Add new fee for ${
        consumerCategories.find(
          (category) => category.value === consumerCategory
        )?.name || consumerCategories[0]?.name
      }`,
      groupColorNeeded: false,
    },
    {
      name: "amount",
      label: `Amount (${currency})`,
      type: "decimal",
      placeholder: "0.00",
      required: true,
      group: `Add new fee for ${
        consumerCategories.find(
          (category) => category.value === consumerCategory
        )?.name || consumerCategories[0]?.name
      }`,
      groupColorNeeded: false,
    },
    {
      name: "description",
      label: "Description",
      type: "textarea",
      required: false,
      placeholder: "Brief description",
      group: `Add new fee for ${
        consumerCategories.find(
          (category) => category.value === consumerCategory
        )?.name || consumerCategories[0]?.name
      }`,
      groupColorNeeded: false,
    },
  ];

  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
  });

  useEffect(() => {
    if (consumerCategories.length > 0 && !consumerCategory) {
      const firstCategory = consumerCategories[0]?.value;
      setConsumerCategory(firstCategory);
      form.reset({
        consumerCategory: firstCategory,
        name: "",
        amount: "",
        description: "",
      });
    }
  }, [consumerCategories, consumerCategory]);

  const handleFieldChange = (fieldName: string, value: any): void => {
    switch (fieldName) {
      case "consumerCategory":
        setConsumerCategory(value);
        break;
    }
  };

  const handleFormSubmit = (data: any): void => {
    const payload = {
      remote_utility_id: Number(remoteUtilityId),
      data: [
        {
          consumer_category: data.consumerCategory,
          fees: [
            {
              name: data.name,
              amount: data.amount,
              description: data.description,
              category_name: consumerCategories.find(
                (category) => category.value === consumerCategory
              )?.name,
            },
          ],
        },
      ],
    };

    createFeeConfiguration(payload);

    form.reset({
      consumerCategory: data.consumerCategory,
      name: "",
      amount: "",
      description: "",
    });
  };

  const handleRemoveFee = (feeCode: string): void => {
    console.log("feeCode", feeCode);
    const payload = {
      remote_utility_id: Number(remoteUtilityId),
      category: consumerCategory,
      fee_code: feeCode,
    };
    deleteFeeConfiguration(payload);
  };

  return (
    <div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <DynamicForm
            fields={formFields}
            form={form}
            gridColumns={1}
            onFieldChange={handleFieldChange}
            onSubmit={handleFormSubmit}
            responsiveColumns={{
              sm: 1,
              md: 1,
            }}
            config={{
              showResetButton: false,
              showSubmitButton: true,
              icon: Plus,
              submitButtonText: `Add Fee Line Item`,
              spacing: "comfortable",
              classes: {
                container: "w-full mb-4",
                label: "block text-sm font-medium text-gray-700 mt-3",
                buttonContainer: "flex justify-end",
                submitButton: "w-full",
                input:
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                select:
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                error: "text-red-600 text-sm mt-1",
                helperText: "text-xs text-gray-500 mt-1",
                number:
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                "multi-select":
                  "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all",
                file:
                  " w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12 text-center " +
                  "hover:border-gray-400 transition-colors cursor-pointer bg-white",
                documents:
                  "w-full flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-12 text-center " +
                  "hover:border-gray-400 transition-colors cursor-pointer bg-white",
              },
            }}
          />
        </div>
        <div>
          <ApplicationProcessFeesList
            fees={categoryWiseFees || []}
            onRemoveFee={handleRemoveFee}
            selectedCategory={consumerCategory}
            currency={currency}
            categoryOptions={consumerCategories}
          />
        </div>
      </div>
    </div>
  );
};

export default ApplicationProcessFeesCreate;
